import { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Slider,
  Stack
} from '@mui/material';
import { LeadFormData } from '../types';

interface LeadFormProps {
  onSubmit: (data: LeadFormData) => void;
  isLoading?: boolean;
  initialData?: LeadFormData | null;
}

const LeadForm = ({ onSubmit, isLoading = false, initialData = null }: LeadFormProps) => {
  const [formData, setFormData] = useState<LeadFormData>({
    location: initialData?.location || '',
    radius: initialData?.radius || 10,
    instructions: initialData?.instructions || '',
    maxResults: initialData?.maxResults || 10
  });

  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormData, string>>>({});

  const handleChange = (field: keyof LeadFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof LeadFormData, string>> = {};
    
    if (!formData.location) {
      newErrors.location = 'Location is required';
    } 
    
    if (!formData.instructions) {
      newErrors.instructions = 'Instructions are required';
    } else if (formData.instructions.length < 10) {
      newErrors.instructions = 'Instructions should be more detailed';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Find Business Leads
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Enter a location (address, city or zipcode), radius, and instructions to find businesses that match your criteria.
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={3}>
          <TextField
            label="Location"
            fullWidth
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            error={!!errors.location}
            helperText={errors.location}
            disabled={isLoading}
            placeholder="Enter a US address, city or zipcode (e.g., Tampa, FL or 33601)"
            margin="normal"
          />
          
          <Box>
            <Typography gutterBottom>
              Search Radius: {formData.radius} miles
            </Typography>
            <Slider
              value={formData.radius}
              onChange={(_, value) => handleChange('radius', value as number)}
              min={1}
              max={50}
              step={1}
              marks={[
                { value: 1, label: '1mi' },
                { value: 10, label: '10mi' },
                { value: 25, label: '25mi' },
                { value: 50, label: '50mi' }
              ]}
              valueLabelDisplay="auto"
              disabled={isLoading}
            />
          </Box>
          
          <Box>
            <Typography gutterBottom>
              Maximum Results: {formData.maxResults}
            </Typography>
            <Slider
              value={formData.maxResults}
              onChange={(_, value) => handleChange('maxResults', value as number)}
              min={10}
              max={500}
              step={10}
              marks={[
                { value: 10, label: '10' },
                { value: 100, label: '100' },
                { value: 250, label: '250' },
                { value: 500, label: '500' }
              ]}
              valueLabelDisplay="auto"
              disabled={isLoading}
            />
          </Box>
          
          <TextField
            label="Instructions"
            fullWidth
            multiline
            rows={4}
            value={formData.instructions}
            onChange={(e) => handleChange('instructions', e.target.value)}
            error={!!errors.instructions}
            helperText={errors.instructions || "Searches are strictly according to these criteria. Be specific."}
            disabled={isLoading}
            placeholder="Describe the specific type of businesses you're looking for (e.g., 'Find all coffee shops that offer vegan options and have outdoor seating')"
            margin="normal"
          />
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            size="large"
            disabled={isLoading}
            fullWidth
            sx={{ mt: 2 }}
          >
            {isLoading ? 'Processing...' : 'Find Leads'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

export default LeadForm; 